import { postLogout } from "./services/accountApi.js";
import { useState, useEffect, useRef } from "react";
import { apiPost } from "./lib/apiClient";
import { APP_FONT_FAMILY } from "./lib/appFont.js";
import { product as brandProduct, company as brandCompany } from "./branding.js";
import { beginIntent, surface, ui, withIntent } from "./lib/telemetry/instrument.js";
import { intentArgs } from "./lib/telemetry/uiIntents.js";
import { drainTelemetry } from "./services/telemetry.js";

// Design note: full-viewport backdrop + centered card is a V1 layout choice.
// Design team should iterate on branding, animation, and card dimensions before GA.

// Fallback palette (dark theme) used only when the parent doesn't pass a
// `colors` prop. Normally App.jsx passes the RAW-HEX palette for the active
// theme (themeColors(theme)) so the gate tracks the user's selected theme
// (dark / dusk / base) — raw hex, because the gate paints before GlobalStyles
// publishes the `--c-*` CSS variables (see the render comment below).
const DEFAULT_COLORS = {
  bg: "#060610",
  panel: "#0c0c1e",
  purple: "#9966dd",
  purpleDim: "#5533aa",
  purpleL: "#cc99ff",
  yellow: "#F7CE46",
  gray: "#667788",
  grayL: "#aabbcc",
  white: "#ffffff",
  red: "#ff4455",
  btnText: "#ffffff",
  loadingOverlay: "rgba(4,4,12,0.85)",
};

/**
 * TosGate — blocks the app until the user accepts all current legal documents.
 *
 * Props:
 *   tos        {object} — tos block from /api/me:
 *                          { current: [{name, displayName, version, url}, ...],
 *                            accepted: {...}, isCurrent: false }
 *   onAccepted {function} — called with the updated tos block (from the 200
 *                           response body) once acceptance is recorded.
 *                           On 409 version_mismatch, called with the new tos
 *                           block so the parent can re-mount TosGate with
 *                           updated docs. On other errors, NOT called.
 *   colors     {object} — OPTIONAL raw-hex theme palette. App.jsx passes
 *                           themeColors(theme) here — RAW hex, not the var(--c-*)
 *                           map `C` — because the gate renders before GlobalStyles
 *                           publishes the `--c-*` CSS variables, so a var() ref
 *                           would resolve to nothing. Falls back to DEFAULT_COLORS
 *                           (dark) when omitted.
 *   onPreviewClose {function} — OPTIONAL. When provided, the gate renders in
 *                           "preview" mode: VISUALLY identical to the real gate
 *                           (no extra chrome), but ACCEPT (and DECLINE) dismiss
 *                           locally WITHOUT posting to /api/tos-accept or
 *                           /auth/logout (so it works with no session and never
 *                           records a spurious acceptance). Used by the debug
 *                           "Replay ToS Gate" button.
 */
export default function TosGate({ tos, onAccepted, onPreviewClose, colors }) {
  const C = colors || DEFAULT_COLORS;
  const preview = typeof onPreviewClose === "function";
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const dialogRef = useRef(null);

  // Focus trap: focus the dialog container on mount, and cycle focus within
  // the dialog so Tab cannot escape into the blocked app.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Exclude disabled buttons/inputs so the focus-trap's first/last computation
    // matches what the browser will actually focus on Tab. The "I accept these
    // terms" button is disabled while a submit is in flight; without
    // :not([disabled]) a disabled control would be treated as `last` while being
    // browser-skipped, letting Tab escape the dialog.
    const FOCUSABLE_SELECTOR =
      'a, button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    // Move focus into the dialog WITHOUT highlighting the first interactive
    // element. Focusing the first focusable (the first document link in the
    // data-driven list) painted a blue focus ring on it the moment the gate
    // appeared; focus the
    // dialog container instead (tabIndex=-1, outline:none) so the trap + screen
    // readers still get focus inside the modal but no ring shows on open.
    dialog.focus();

    function onKeyDown(e) {
      if (e.key !== "Tab") return;
      const all = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (all.length === 0) return;
      const first = all[0];
      const last = all[all.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }

    // telemetry-gesture: Tab focus trap inside the gate, not an operation
    dialog.addEventListener("keydown", onKeyDown);
    return () => dialog.removeEventListener("keydown", onKeyDown);
  }, []);

  const docs = tos?.current ?? [];

  // Decline: in preview, just dismiss. In the real gate, the user can't use the
  // service without accepting, so declining signs them out (mirrors the SIGN OUT
  // flow in App.jsx).
  function handleDecline() {
    if (submitting) return;
    if (preview) {
      onPreviewClose();
      return;
    }
    // Mark submitting BEFORE the async logout starts so both buttons disable
    // immediately. Without this, repeated DECLINE clicks would fire multiple
    // logout POSTs, and ACCEPT would still be enabled mid-logout.
    setSubmitting(true);
    setError(null);
    const intent = withIntent(
      "tos.decline",
      (it) => it,
      {},
      { origin: "tos.decline", surface: "tos" },
    );
    (async () => {
      // The decline is queued, not sent: upload it while the session still exists. Bounded
      // and failure-tolerant (drainTelemetry), so the logout always follows.
      await drainTelemetry();
      try {
        const r = await postLogout({ intentId: intent.id });
        const d = await r.json().catch(() => ({}));
        window.location.href = d.logoutUrl || "/";
      } catch (_e) {
        window.location.href = "/";
      }
    })();
  }

  async function handleAccept() {
    if (submitting) return;

    // Preview/debug mode: just dismiss. No server round-trip — there may be no
    // session (local dev), and re-recording an acceptance the user already made
    // would be pointless.
    if (preview) {
      onPreviewClose();
      return;
    }

    setSubmitting(true);
    setError(null);

    const body = {
      accepted: docs.map((d) => ({ name: d.name, version: d.version })),
    };

    const intent = beginIntent(
      "tos.accept",
      { docs: docs.length },
      { origin: "tos.accept", surface: "tos" },
    );
    const result = await apiPost("/api/tos-accept", body, ...intentArgs(intent));
    intent.end(result.ok && result.status === 200 && result.body?.tos ? "ok" : "error");
    // Note: 401 is handled by apiPost itself (redirect + never-resolving promise).
    // We never reach here on 401.

    if (result.ok && result.status === 200) {
      const newTos = result.body?.tos;
      if (!newTos) {
        // 200 with empty / non-JSON / unexpected body shape. Surface as a
        // soft error rather than crashing on result.body.tos.
        setError("Couldn't record your acceptance. Please try again.");
        setSubmitting(false);
        return;
      }
      // Success: parent re-keys/unmounts TosGate when it processes the new tos.
      // Don't reset submitting — component is about to unmount.
      onAccepted(newTos);
      return;
    }

    if (result.status === 409 && result.body?.error === "version_mismatch") {
      // A newer manifest is available. Surface it to the parent so it can
      // re-mount TosGate with the updated doc list. Reset local state so the
      // new mount starts fresh (App.jsx passes a manifest-versioned key, so
      // a doc/version change forces a fresh mount regardless).
      const newTos = result.body?.tos;
      setSubmitting(false);
      if (!newTos) {
        setError("Couldn't record your acceptance. Please try again.");
        return;
      }
      onAccepted(newTos);
      return;
    }

    // Any other error (5xx, unexpected 4xx, etc.)
    setError("Couldn't record your acceptance. Please try again.");
    setSubmitting(false);
  }

  // Backdrop + card track the active theme via the raw-hex palette in `colors`
  // (App.jsx passes themeColors(theme)). The gate renders before GlobalStyles
  // mounts, so the `--c-*` CSS variables don't exist yet — every color here must
  // be a resolved hex value, never a var() reference. Raw hex keeps the whole
  // gate — backdrop (bg), card (panel), and the text/border/button colors below —
  // on one consistent theme, so nothing lands dark-on-dark (light theme) or
  // light-on-light. Same surface tokens as ModalShell: bg backdrop, panel card.

  return (
    // Full-viewport backdrop intercepts all pointer events outside the card.
    <div
      {...surface("tos")}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: C.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: APP_FONT_FAMILY,
        color: C.white,
      }}
    >
      {/* Card */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tos-gate-title"
        tabIndex={-1}
        style={{
          outline: "none",
          background: C.panel,
          border: `1px solid ${C.purpleDim}`,
          borderRadius: 12,
          padding: "26px 32px",
          maxWidth: 480,
          width: "90vw",
          boxShadow: "0 6px 28px rgba(0,0,0,.8)",
        }}
      >
        {/* Wordmark */}
        <div
          id="tos-gate-title"
          style={{
            fontSize: 24,
            fontWeight: 600,
            marginBottom: 12,
            color: C.white,
          }}
        >
          Welcome to {brandProduct.prose}
        </div>

        {/* Header */}
        <div
          style={{
            fontSize: 13,
            color: C.grayL,
            lineHeight: 1.5,
            marginBottom: 12,
          }}
        >
          Before you start, please review and accept {brandCompany.name}&rsquo;s terms:
        </div>

        {/* Document links */}
        <ul
          style={{
            listStyle: "none",
            margin: "0 0 18px",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {docs.map((doc) => (
            <li key={doc.name}>
              <a
                {...ui("tos.document", doc.name)}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: C.purpleL,
                  textDecoration: "none",
                  fontSize: 13,
                  letterSpacing: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = C.yellow;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = C.purpleL;
                }}
              >
                {doc.displayName}
              </a>
            </li>
          ))}
        </ul>

        {/* Inline error message */}
        {error && (
          <div
            style={{
              fontSize: 12,
              color: C.red,
              marginBottom: 12,
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        )}

        {/* Decline (left, ghost) + Accept (right, filled). Both labels are
            sentence case now; letter-spacing eased to 0.5px (was 1.5px when
            both were all-caps) so the letters don't drift apart unnaturally.
            Accept claims a wider share of the row (flex 2 vs decline's flex 1)
            because its label ("I accept these terms") is longer. */}
        <div style={{ display: "flex", gap: 12 }}>
          <button
            {...ui("tos.decline")}
            type="button"
            onClick={handleDecline}
            disabled={submitting}
            style={{
              flex: 1,
              background: "transparent",
              color: C.grayL,
              border: `1px solid ${C.purpleDim}`,
              borderRadius: 8,
              padding: "11px 0",
              fontFamily: "inherit",
              fontSize: 12,
              letterSpacing: 0,
              fontWeight: "bold",
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.6 : 1,
              transition: "opacity 0.15s, border-color 0.15s, color 0.15s",
            }}
          >
            Decline
          </button>
          <button
            {...ui("tos.accept")}
            type="button"
            onClick={handleAccept}
            disabled={submitting}
            style={{
              flex: 2,
              background: submitting ? C.purpleDim : C.purple,
              // Mirror the App.jsx pattern (C.btnText || "#fff"): the `base`
              // theme has C.purple === "#ffffff" and pairs it with
              // C.btnText === "#000000", so a hard-coded "#fff" here would
              // render the label white-on-white and invisible.
              color: C.btnText || "#fff",
              border: "none",
              borderRadius: 8,
              padding: "11px 0",
              fontFamily: "inherit",
              fontSize: 12,
              letterSpacing: 0,
              fontWeight: "bold",
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.6 : 1,
              transition: "opacity 0.15s, background 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            {submitting ? "Saving…" : "I accept these terms"}
          </button>
        </div>
      </div>
    </div>
  );
}
